import { Box, makeStyles, styled } from '@material-ui/core';
import { Collection } from '@type-r/models';
import { globalStore } from 'api';
import { AppLink, asCurrency, asDate, asTimelessDate, colors, div, Flex, FormCheckbox, Link, PopoverValue } from 'lqx-react';
import { isEmbeddedApp } from 'lqx-react/src';
import * as React from 'react';
import { execSetEmailSentFlag, RecsInvoice } from 'src/app/pages/inmatch/api';
import { invoiceStatusColor } from '../../src/app/pages/invoices/common';
import { RowH2Bold } from '../inmatch';
import { EmailButton, InblockIcon, InvoicePaymentButton, RAButton, RIButton, SocCommentStatus } from './iconography';

export const InvoiceTile = (selected: Collection<RecsInvoice>, raLinkRoot = '/remittance-advices/details/', withStatus?: boolean, withSocComments?: boolean) =>
    ({ data, style, index }) => {
        const invoice: RecsInvoice = data.items[index];

        const setEmailInvoiceToFalse = () => {
            invoice.set({ ...invoice, emailSentOn: null });
            execSetEmailSentFlag([invoice.id], false);
        }

        const iconClasses = inblockIconStyles();
        return (
            <div style={style}>
                <Flex row alignCenter>
                    <FormCheckbox $checked={selected.$includes(invoice)} />

                    <InvoiceRow
                        icon={
                            <DigitizedIcon invoice={ invoice }>
                                <UnmatchedIcon />
                            </DigitizedIcon>
                        }
                        invoice={invoice}
                        paymentId={
                            <Link onClick={() => void 0}>
                                Select
                            </Link>
                        }
                    >
                        <OverrideValue
                            value={asCurrency(invoice.invoicedAmount, invoice.currency)}
                            override={
                                !invoice.unreconciledAmount ?
                                    ' ' :
                                    invoice.invoicedAmount != invoice.unreconciledAmount ?
                                        asCurrency(invoice.unreconciledAmount, invoice.currency) :
                                        null
                            }
                        />

                        <OverrideValue
                            value={asTimelessDate(invoice.invoiceDueDate)}
                            override={
                                !invoice.unreconciledAmount ?
                                    ' ' :
                                    invoice.promiseToPayDate ?
                                    asTimelessDate(invoice.promiseToPayDate) :
                                        null
                            }

                            red={+(invoice.age > 0)}
                        />

                        {withStatus ?
                            <ListField>
                                {asTimelessDate((invoice as any).invoiceSentDateUtc)}
                            </ListField>
                            : null}

                        <Flex grow />

                        {withStatus ?
                            <DisplayStatus color={invoiceStatusColor(invoice.calculatedStatus)}>
                                {invoice.displayStatus}
                            </DisplayStatus>
                            : null}

                        {invoice.emailSentOn && <EmailButton onClick={setEmailInvoiceToFalse} />}

                        <RAButton remittanceAdviceIds={invoice.remittanceAdviceIds} raLinkRoot={raLinkRoot} />
                        <RIButton paymentInfoIds={invoice.paymentInfoIds} />

                        <InvoicePaymentButton matchedPayments={invoice.matchedPayments} />
                        {withSocComments && <SocCommentStatus hasSocComment={invoice.hasSocComments} />}
                    </InvoiceRow>
                </Flex>
            </div>
        )
    }

export const DigitizedIcon = ({invoice, children} : {invoice: RecsInvoice, children: React.ReactNode}) => {
    const iconClasses = inblockIconStyles();

    return invoice.isDigitized
            ? <Flex alignCenter>
                    <InblockIcon src={'/assets/images/inblock_logo.png'} classes={iconClasses} />
                    { children }
                </Flex>
            :<Flex alignCenter>
                    <EmptySpace width={24} />
                    { children }
            </Flex>
};

export const inblockIconStyles = makeStyles({
    root: {
        width: '24px',
        height: '24px',
        marginRight: '5px'
    }
});

export const EmptySpace = div({
    width: ( props : any ) => props.width,
    height: ( props : any ) => props.height
})

export const HeaderField = div({
    display: 'flex',
    flexDirection: 'row',
    paddingLeft: 8,
    paddingRight: 8,
    minWidth: (props: any) => props.width || '15em',
    fontSize: 12,
    color: 'black',
    fontWeight: 600
})

export const HeaderContainer = div({
    padding: '0px 6px 0px 6px',
    display: 'flex',
    flex: '1 auto',
    flexDirection: 'row',
    alignItems: 'center',
    boxSizing: 'border-box',
    borderRadius: '0 2px 2px',
    margin: '2px 0 2px 0',
    backgroundColor: 'white',
    border: '1px solid #F1F5F8',
    boxShadow: '1px 0 2px 0 rgba(0,0,0,0.04)',
})

export const InvoiceTileHeader = () =>
    <Flex row>
        <EmptySpace width={40} height={40} />
        <HeaderContainer>
            <EmptySpace width={60} height={33} />
            <HeaderField width={160}>Invoice number</HeaderField>
            <HeaderField>Amount</HeaderField>
            <HeaderField>Due/Promise to pay</HeaderField>
            <HeaderField>Sent Date</HeaderField>

            <Flex grow />
            <HeaderField width={120}>Status</HeaderField>
            <HeaderField width={45}>RA</HeaderField>
            <HeaderField width={40}>PI</HeaderField>
            <HeaderField width={60}>Pmt</HeaderField>
            <EmptySpace width={60}></EmptySpace>
        </HeaderContainer>
    </Flex>

const UnmatchedIcon = styled(
    (props) => <div {...props}>·</div>
)({
    color: '#FF274B',
    width: 14,
    cursor: 'default',
    height: 21,
    textAlign: 'center',
    fontWeight: 800,
})

const OverrideValue = ({ value, override, red = undefined }) =>
    <ListField>
        {override ? <>
            <StrikeThrough override={override}>{value}</StrikeThrough>
            <BoldField red={red}>{override} </BoldField>
        </> :
            <BoldField red={red}>{value} </BoldField>
        }
    </ListField>

export const ListField = div({
    display: 'flex',
    flexDirection: 'row',
    paddingLeft: 8,
    paddingRight: 8,
    minWidth: '15em',
    fontSize: 12,
    color: '#A0A0A0'
})

const DisplayStatus = div({
    fontSize: 14,
    color: (props: any) => props.color ? props.color : colors.grey
});

export const BoldField = div({
    fontSize: 12,
    color: (props: any) => props.red ? colors.red : '#404040',
    fontWeight: (props: any) => props.red ? 600 : 400
})

const StrikeThrough = div({
    textDecoration: (props: any) => props.override ? 'line-through' : 'none',
    marginRight: 8
})

export const InvoiceRow = ({ icon, invoice, children, compact, middle, paymentId }: {
    icon: React.ReactNode
    invoice: RecsInvoice
    children: React.ReactNode
    compact?: boolean,
    middle?: React.ReactNode,
    paymentId?: any
}) =>
    <ListItem
        icon={icon}
        title={<RowH2Bold>{invoice.invoiceNumber}</RowH2Bold>}
        extensionBox={<InvoiceDetails invoice={invoice} paymentId={paymentId} />}
    >
        {children}
    </ListItem>

export const ListItem = (props: {
    title: React.ReactNode,
    icon: React.ReactNode,
    extensionBox: JSX.Element | string,
    children: React.ReactNode
}) => {
    const classes = useStyles(props);
    return (
        <div className={classes.root}>
            <div className={classes.icon}>{props.icon}</div>

            <div className={classes.fixedWidth}>
                <PopoverValue label={props.title} anchorAlign="left" transformAlign="left">
                    {props.extensionBox}
                </PopoverValue>
            </div>


            {props.children}
        </div>
    )
}

const useStyles = makeStyles({
    root: {
        padding: '0px 6px 0px 6px',
        display: 'flex',
        flex: '1 auto',
        flexDirection: 'row',
        alignItems: 'center',
        boxSizing: 'border-box',
        borderRadius: '0 2px 2px',
        margin: '2px 0 2px 0',
        backgroundColor: '#F9F9F9',
        border: '1px solid #F1F5F8',
        boxShadow: '1px 0 2px 0 rgba(0,0,0,0.04)',
        /*
                '& button' : {
                    visibility: 'hidden',
                },
                '&:hover button' : {
                    visibility: 'visible',
                } */
    },

    fixedWidth: {
        width: '10em'
    },

    icon: {
        margin: 6
    }
})

export const InvoiceDetails = ({ invoice, paymentId }: {
    invoice: RecsInvoice,
    paymentId: string
}) => {

    const getInternalStatus = (invoiceInternalId) => {
        const statusInfo = globalStore.getInternalInvoiceStatusInfo(invoiceInternalId);
        return (statusInfo)
            ? statusInfo.internalStatus
            : '-';
    }

    return (
        <>
            <Box display="flex" flexDirection="row" p={2} pb={1}>
                <DataField label="legal entity" value={invoice.customer.name} />
                <DataField label="projected payment" value={asCurrency(invoice.unreconciledAmount, invoice.currency)} />
                <DataField label="estimated value date" value="-" />
                <DataField label="Internal Status" value={getInternalStatus(invoice.internalStatusId)} />
            </Box>
            <Box display="flex" flexDirection="row" p={2} pb={1}>
                <DataField label="projected pay date" value={asTimelessDate(invoice.estimatedPayDate)} />
                <DataField label="inv due" value={asTimelessDate(invoice.invoiceDueDate)} />                
                {paymentId ? <DataField label="Linked Payment" value={paymentId} /> : null}
            </Box>
        </>
    );
}

export const DataField = ({ label, value }: {
    label: any,
    value: any
}) => {
    return (
        <Box display="flex" flexDirection="column" mr={2} my={0.5} minWidth={100}>
            <Label>
                {label}
            </Label>
            <Value>
                {value}
            </Value>
        </Box>
    );
};

export const Label = styled('span')({
    color: '#728A97',
    marginRight: '1em',
    fontFamily: 'Montserrat',
    fontSize: 10,
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
});

const Value = styled('div')({
    color: '#000',
    fontFamily: 'Montserrat',
    fontSize: 14,
    fontWeight: 500,
});
